import {
  DashboardViewPreference,
  DashboardViewPreferenceValues,
} from './../users/usersAPI';
import { PaletteMode } from '@mui/material';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../app/store';
import { store } from '../../app/store';
import {
  usersApi,
  RawUserPreferences,
  DisableDashboardMapPreference,
  SaveCustomThemePreference,
} from '../users/usersAPI';
import { getCookie } from '../../utils/cookieHandlers';

export interface IExpiredStore {
  storeName: string;
  storeNum: number;
  expirationDate: string;
}
export type IExpiredStoresList = IExpiredStore[];

export type OldCookiesColorPallete = {
  primary: {
    main: string;
  };
  secondary: {
    main: string;
  };
  error: {
    main: string;
  };
  warning: {
    main: string;
  };
  info: {
    main: string;
  };
  success: {
    main: string;
  };
};

function isOldCookiesColorPalette(obj: unknown): obj is OldCookiesColorPallete {
  return (
    obj !== null &&
    obj !== undefined &&
    typeof obj === 'object' &&
    'primary' in obj &&
    !('background' in obj)
  );
}

export type EditablePallete = {
  primary: {
    main: string;
  };
  secondary: {
    main: string;
  };
  error: {
    main: string;
  };
  warning: {
    main: string;
  };
  info: {
    main: string;
  };
  success: {
    main: string;
  };
  background: {
    default: {
      light: string;
      dark: string;
    };
    paper: {
      light: string;
      dark: string;
    };
  };
};

export type EditableCellTypeOrder = Array<string>;

function isNewCookiesColorPalette(obj: unknown): obj is EditablePallete {
  return (
    obj !== null &&
    obj !== undefined &&
    typeof obj === 'object' &&
    'primary' in obj &&
    'background' in obj
  );
}

export type CookiesColorPallete = OldCookiesColorPallete | EditablePallete;
export interface ViewState {
  mode: PaletteMode;
  palette: EditablePallete;
  showDrawer: boolean;
  showSites: boolean;
  showSessionExpiredDialog: boolean;
  showExpiredSitesDialog: boolean;
  hasShownExpiredSitesDialog: boolean;
  expiredSites: IExpiredStoresList;
  soonToExpireSites: IExpiredStoresList;
  showUnauthorizedSiteAccess: boolean;
  showHowToEditReportingDialog: boolean;
  showHowToAccessLeoDialog: boolean;
  showHowToAccessTerminalModeDialog: boolean;
  showHowToCreateAdvancedScheduleDialog: boolean;
  showTerminalModeUnderMaintenanceDialog: boolean;
  terminalModeUnderMaintenance: boolean;
  showLeoCloudUnderMaintenance: boolean;
  siteToFocus: number | null;
  showMaintenanceBanner: boolean;
  dashboardViewPreference: DashboardViewPreferenceValues;
  customCellTypeOrder: EditableCellTypeOrder;
  isSmallDevice: boolean;
  isMediumDevice: boolean;
  isDevToolsOpen: boolean;
  isTestingEnv: boolean;
}

// this looks pretty good. maybe darken the primary and secondary slightly
// {
//   "colorPalette": {
//     "primary": {
//       "main": "#8ff0a4"
//     },
//     "secondary": {
//       "main": "#99c1f1"
//     },
//     "error": {
//       "main": "#f66151"
//     },
//     "success": {
//       "main": "#26a269"
//     },
//     "warning": {
//       "main": "#ffbe6f"
//     },
//     "info": {
//       "main": "#f9f06b"
//     },
//     "background": {
//       "default": {
//         "light": "#fafafa",
//         "dark": "#303030"
//       },
//       "paper": {
//         "light": "#ffffff",
//         "dark": "#424242"
//       }
//     }
//   }
// }
export const PALETTE_DEFAULT: EditablePallete = {
  // CURRENT THEME
  primary: {
    // orignal in comments
    // HLGREEN = #2a652a
    main: '#548354', // '#158eec',
  },
  secondary: {
    main: '#158eec', // '#ec7215',
  },
  error: {
    main: '#f44336',
  },
  success: {
    main: '#2e7d32', // #40bf6d <- This is a great looking green text color but not so good on the sidebar
  },
  warning: {
    main: '#ed6c02',
  },
  info: {
    main: '#f5f3e5', // '#0288d1',
  },
  background: {
    default: {
      light: '#fafafa',
      dark: '#303030',
    },
    paper: {
      light: '#ffffff',
      dark: '#424242',
    },
  },
};

const COOKIE_NAME = 'ColorPalette';

// need to Modify Old palettes to include the background
const getColorPaletteFromCookies = () => {
  const cookies = document.cookie.split(';');

  for (const cookie of cookies) {
    const [cookieName, cookieVal] = cookie.trim().split('=');

    if (cookieName === COOKIE_NAME) {
      // need to modify old palettes to include background
      const colorPalette = JSON.parse(cookieVal) as CookiesColorPallete;
      console.info({ colorPalette });
      if (isOldCookiesColorPalette(colorPalette)) {
        const updatedPalette: EditablePallete = {
          ...colorPalette,
          background: PALETTE_DEFAULT.background,
        };
        console.info('modified old colorPalette');
        return updatedPalette;
      } else if (isNewCookiesColorPalette(colorPalette)) {
        console.info('no modification to palettte needed');
        return colorPalette;
      }
    }
  }

  return null;
};
// const saveColorPaletteToCookies = (colorPalette: EditablePallete) => {
//   document.cookie = `${COOKIE_NAME}=${JSON.stringify(
//     colorPalette,
//   )}; expires=Thu, 18 Dec 2100 12:00:00 UTC; path=/; samesite=strict;`;
// };

const getDisableDashboardMapCookie = () => {
  const COOKIE_NAME = 'disableDashboardMap';

  const cookieValueUnparsed = getCookie(COOKIE_NAME);

  if (cookieValueUnparsed) {
    return cookieValueUnparsed === 'true';
  }

  return null;
};
// const updateDisableDashboardMap = (value: boolean) => {
//   const COOKIE_NAME = "disableDashboardMap";
//   setCookie(COOKIE_NAME, value.toString());
// };

const checkIfTestingEnv = () => {
  if (import.meta.env.VITEST_POOL_ID !== undefined) {
    return true;
  }
  return false;
};

const initialState: ViewState = {
  mode: 'light',
  showDrawer: true,
  showSites: false,
  showSessionExpiredDialog: false,
  hasShownExpiredSitesDialog: false,
  showExpiredSitesDialog: false,
  showUnauthorizedSiteAccess: false,
  showHowToEditReportingDialog: false,
  showHowToAccessLeoDialog: false,
  showHowToAccessTerminalModeDialog: false,
  showTerminalModeUnderMaintenanceDialog: false,
  terminalModeUnderMaintenance: false,
  showLeoCloudUnderMaintenance: false,
  showHowToCreateAdvancedScheduleDialog: false,
  expiredSites: [],
  soonToExpireSites: [],
  siteToFocus: null,
  isSmallDevice: false,
  isMediumDevice: false,
  // TODO Create method to check for preferences in the cookies and apply them to the DB if they don't exist
  palette: PALETTE_DEFAULT,
  // disableDashboardMap: false,
  dashboardViewPreference: 'map',
  customCellTypeOrder: [], // if empty cell type order, then use default (jacob's) sorting method
  isDevToolsOpen: false,
  isTestingEnv: checkIfTestingEnv(),
  showMaintenanceBanner: false,
};

export const viewSlice = createSlice({
  name: 'view',
  initialState,
  reducers: {
    toggleMode: (state) => {
      if (state.mode === 'dark') state.mode = 'light';
      else state.mode = 'dark';
    },
    toggleDrawer: (state) => {
      if (state.showDrawer) {
        state.showDrawer = false;
      } else {
        state.showDrawer = true;
      }
    },
    toggleSitesDrawer: (state) => {
      // need to make sure this gets toggled off when a user logs out
      if (state.showSites) {
        state.showSites = false;
      } else {
        state.showSites = true;
      }
    },
    showSessionExpiredDialog: (state, action: PayloadAction<boolean>) => {
      state.showSessionExpiredDialog = action.payload;
    },
    closeSessionExpiredDialog: (state) => {
      state.showSessionExpiredDialog = false;
    },
    closeExpiredSitesDialog: (state) => {
      state.showExpiredSitesDialog = false;
    },
    showExpiredSitesDialog: (
      state,
      action: PayloadAction<{
        expiredSites: IExpiredStoresList;
        soonToExpireSites: IExpiredStoresList;
      }>,
    ) => {
      state.expiredSites = action.payload.expiredSites;
      state.soonToExpireSites = action.payload.soonToExpireSites;
      state.showExpiredSitesDialog = true;
    },
    setHasShownExpiredSitesDialog: (state, action: PayloadAction<boolean>) => {
      state.hasShownExpiredSitesDialog = action.payload;
    },
    forceCloseDrawerAndSites: (state) => {
      state.showSites = false;
      state.showDrawer = false;
    },
    focusSite: (state, action: PayloadAction<number>) => {
      state.showDrawer = true;
      state.showSites = true;
      state.siteToFocus = action.payload;
    },
    clearOutFocusSiteState: (state) => {
      state.siteToFocus = null;
    },
    setIsSmallDevice: (state, action: PayloadAction<boolean>) => {
      state.isSmallDevice = action.payload;
    },
    setIsMediumDevice: (state, action: PayloadAction<boolean>) => {
      state.isMediumDevice = action.payload;
    },
    setDashboardViewPreference: (
      state,
      action: PayloadAction<DashboardViewPreferenceValues>,
    ) => {
      state.dashboardViewPreference = action.payload;
    },
    handleShowUnauthorizedSiteAccess: (state) => {
      state.showUnauthorizedSiteAccess = true;
    },
    handleHideUnauthorizedSiteAccess: (state) => {
      state.showUnauthorizedSiteAccess = false;
    },
    handleShowHowToEditReportingDialog: (state) => {
      state.showHowToEditReportingDialog = true;
    },
    handleHideHowToEditReportingDialog: (state) => {
      state.showHowToEditReportingDialog = false;
    },
    handleShowHowToAccessLeoDialog: (state) => {
      state.showHowToAccessLeoDialog = true;
    },
    handleHideHowToAccessLeoDialog: (state) => {
      state.showHowToAccessLeoDialog = false;
    },
    handleShowHowToAccessTerminalModeDialog: (state) => {
      state.showHowToAccessTerminalModeDialog = true;
    },
    handleHideHowToAccessTerminalModeDialog: (state) => {
      state.showHowToAccessTerminalModeDialog = false;
    },
    handleShowHowToCreateAdvancedScheduleDialog: (state) => {
      state.showHowToCreateAdvancedScheduleDialog = true;
    },
    handleHideHowToCreateAdvancedScheduleDialog: (state) => {
      state.showHowToCreateAdvancedScheduleDialog = false;
    },
    setDevToolsOpen: (state) => {
      // code is setup in App.tsx for this to be used
      state.isDevToolsOpen = true;
    },
    setDevToolsClosed: (state) => {
      state.isDevToolsOpen = false;
    },
    setShowMaintenanceBanner: (state, action: PayloadAction<boolean>) => {
      state.showMaintenanceBanner = action.payload;
    },
    setLeoCloudUnderMaintenance: (state, action: PayloadAction<boolean>) => {
      state.showLeoCloudUnderMaintenance = action.payload;
    },
    setTerminalModeUnderMaintenance: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.terminalModeUnderMaintenance = action.payload;
    },
    setShowTerminalModeUnderMaintenanceDialog: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.showTerminalModeUnderMaintenanceDialog = action.payload;
    },
  },
  extraReducers(builder) {
    builder.addMatcher(
      // sets up user preferences after successful request
      usersApi.endpoints.getUserPreferences.matchFulfilled,
      (state, { payload }) => {
        if (payload?.length) {
          const newPreferences: RawUserPreferences[] = [];
          const dashboardViewPreferenceExists =
            payload.find(
              (preference) =>
                preference.preference_name === 'dashboardViewPreference',
            ) !== undefined;
          for (const preference of payload) {
            switch (preference.preference_name) {
              case 'customTheme':
                state.palette = preference.preference_value;
                break;
              case 'disableDashboardMap':
                // state.disableDashboardMap = preference.preference_value;
                // converting to new dashboardViewPreference
                if (!dashboardViewPreferenceExists) {
                  console.info('generating dashboardViewPreference');
                  newPreferences.push({
                    preference_category: 'layout',
                    preference_name: 'dashboardViewPreference',
                    preference_value: preference.preference_value
                      ? 'tables'
                      : 'map',
                  } as DashboardViewPreference);
                  state.dashboardViewPreference = preference.preference_value
                    ? 'tables'
                    : 'map';
                }
                break;
              case 'dashboardViewPreference':
                state.dashboardViewPreference = preference.preference_value;
                break;
              case 'customCellTypeOrder':
                state.customCellTypeOrder = preference.preference_value;
                break;
            }
          }
          if (newPreferences?.length) {
            setTimeout(() => {
              const userId = store.getState().auth.userId;
              if (userId > 0) {
                console.info('writing dashboardViewPreference');
                store.dispatch(
                  usersApi.endpoints.setUserPreferences.initiate({
                    preferences: newPreferences,
                    user_id: userId,
                  }),
                );
                console.info('invalidated dashboardViewPreference cache');
                store.dispatch(
                  usersApi.util.invalidateTags(['UserPreferences']),
                );
              } else {
                console.info('Missing user ID???');
              }
            }, 500);
          }
        } else {
          // determine if they had preferences already saved in their cookie
          const disableMapCookie = getDisableDashboardMapCookie();
          const colorPaletteCookie = getColorPaletteFromCookies();
          // will i need to add cookies for cell type order too?

          const newPreferences: RawUserPreferences[] = [];
          if (disableMapCookie !== null) {
            newPreferences.push({
              preference_category: 'layout',
              preference_name: 'disableDashboardMap',
              preference_value: disableMapCookie,
            } as DisableDashboardMapPreference);
          }
          if (colorPaletteCookie !== null) {
            newPreferences.push({
              preference_category: 'appearance',
              preference_name: 'customTheme',
              preference_value: JSON.stringify(colorPaletteCookie),
            } as SaveCustomThemePreference);
          }

          if (newPreferences?.length) {
            const userId = store.getState().auth.userId;
            if (userId > 0) {
              store.dispatch(
                usersApi.endpoints.setUserPreferences.initiate({
                  preferences: newPreferences,
                  user_id: userId,
                }),
              );
              store.dispatch(usersApi.util.invalidateTags(['UserPreferences']));
            }
          }
        }
      },
    );
  },
});

export const {
  toggleMode,
  toggleDrawer,
  toggleSitesDrawer,
  showSessionExpiredDialog,
  closeSessionExpiredDialog,
  closeExpiredSitesDialog,
  showExpiredSitesDialog,
  setHasShownExpiredSitesDialog,
  forceCloseDrawerAndSites,
  focusSite,
  clearOutFocusSiteState,
  setIsSmallDevice,
  setIsMediumDevice,
  handleShowUnauthorizedSiteAccess,
  handleHideUnauthorizedSiteAccess,
  handleShowHowToEditReportingDialog,
  handleHideHowToEditReportingDialog,
  handleShowHowToAccessLeoDialog,
  handleHideHowToAccessLeoDialog,
  handleShowHowToAccessTerminalModeDialog,
  handleHideHowToAccessTerminalModeDialog,
  handleShowHowToCreateAdvancedScheduleDialog,
  handleHideHowToCreateAdvancedScheduleDialog,
  setDevToolsOpen,
  setDevToolsClosed,
  setDashboardViewPreference,
  setTerminalModeUnderMaintenance,
  setLeoCloudUnderMaintenance,
  setShowTerminalModeUnderMaintenanceDialog,
  setShowMaintenanceBanner,
} = viewSlice.actions;

export const selectTheme = (state: RootState) => state.view.mode;
export const selectCustomThemePalette = (state: RootState) =>
  state.view.palette;

export const selectShowSessionExpiredDialog = (state: RootState) =>
  state.view.showSessionExpiredDialog;
export const selectShowExpiredSitesDialog = (state: RootState) =>
  state.view.showExpiredSitesDialog;
export const selectHasShownExpiredSitesDialog = (state: RootState) =>
  state.view.hasShownExpiredSitesDialog;

export const selectShowHowToEditReportingDialog = (state: RootState) =>
  state.view.showHowToEditReportingDialog;
export const selectShowHowToAccessLeoDialog = (state: RootState) =>
  state.view.showHowToAccessLeoDialog;
export const selectShowHowToAccessTerminalModeDialog = (state: RootState) =>
  state.view.showHowToAccessTerminalModeDialog;
export const selectShowHowToCreateAdvancedScheduleDialog = (
  state: RootState,
) => state.view.showHowToCreateAdvancedScheduleDialog;
export const selectIsShowingSomeHowToDialog = (state: RootState) =>
  state.view.showHowToEditReportingDialog ||
  state.view.showHowToAccessLeoDialog ||
  state.view.showHowToAccessTerminalModeDialog ||
  state.view.showHowToCreateAdvancedScheduleDialog;

export const selectShowUnauthorizedSiteAccess = (state: RootState) =>
  state.view.showUnauthorizedSiteAccess;
export const selectExpiredSitesList = (state: RootState) =>
  state.view.expiredSites;
export const selectSoonToExpireSites = (state: RootState) =>
  state.view.soonToExpireSites;
export const selectShowDrawer = (state: RootState) => state.view.showDrawer;
export const selectShowSitesDrawer = (state: RootState) => state.view.showSites;
export const selectSiteToFocus = (state: RootState) => state.view.siteToFocus;
export const selectDashboardViewPreference = (state: RootState) =>
  state.view.dashboardViewPreference;
export const selectCustomCellTypeOrder = (state: RootState) =>
  state.view.customCellTypeOrder;

export const selectIsTesting = (state: RootState) => state.view.isTestingEnv;

export const selectIsDevToolsOpen = (state: RootState) =>
  state.view.isDevToolsOpen; // CURRENTLY NOT USED (LOGIC IS MOVED INTO THE RemoteAccess.tsx) commented out codes in App.tsx

export const selectDeviceSize = (state: RootState) => ({
  isSmallDevice: state.view.isSmallDevice,
  isMediumDevice: state.view.isMediumDevice,
});

export const selectShowTerminalModeUnderMaintenanceDialog = (
  state: RootState,
) => state.view.showTerminalModeUnderMaintenanceDialog;
export const selectShowMaintenanceBanner = (state: RootState) =>
  state.view.showMaintenanceBanner;

export const selectTerminalModeUnderMaintenance = (state: RootState) =>
  state.view.terminalModeUnderMaintenance;
export const selectLeoCloudUnderMaintenance = (state: RootState) =>
  state.view.showLeoCloudUnderMaintenance;
export const selectIsMaintenanceActive = createSelector(
  [selectTerminalModeUnderMaintenance, selectLeoCloudUnderMaintenance],
  (terminalModeUnderMaintenance, leoCloudUnderMaintenance) => {
    return terminalModeUnderMaintenance || leoCloudUnderMaintenance;
  },
);

export default viewSlice.reducer;
